import type { ApiErrorBody, AuthenticatedUser } from '@iculogbook/shared';

/**
 * The auth API client.
 *
 * Deliberately thin: no interceptors, no retry, no cache. Session state lives
 * in a cookie the browser manages, and entry data never comes through here —
 * that is Dexie's job, from phase 8.
 */

/** A failure the server described. `code` maps to copy in `errorCopy.ts`. */
export class ApiError extends Error {
  readonly code: string | null;
  readonly status: number;

  constructor(code: string | null, status: number) {
    super(`Request failed: ${code ?? 'unknown'} (${String(status)})`);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
  }
}

/** `fetch` itself failed — offline, DNS, server unreachable. */
export class NetworkError extends Error {
  constructor() {
    super('Network request failed');
    this.name = 'NetworkError';
  }
}

/**
 * Exported so `features/admin/adminApi.ts` can reuse the same fetch shape —
 * same-origin credentials, the JSON envelope, `ApiError`/`NetworkError` — for
 * one more small API surface without a second copy of this function.
 */
export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;

  try {
    response = await fetch(path, {
      ...init,
      // Same origin in production and through the Vite proxy in development,
      // so the session cookie travels without CORS being involved at all.
      credentials: 'same-origin',
      headers: {
        ...(init?.body === undefined ? {} : { 'content-type': 'application/json' }),
        ...init?.headers,
      },
    });
  } catch {
    throw new NetworkError();
  }

  if (response.status === 204) return undefined as T;

  const body: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const code = (body as ApiErrorBody | null)?.error?.code ?? null;
    throw new ApiError(code, response.status);
  }

  return body as T;
}

type UserResponse = { user: AuthenticatedUser };

export const authApi = {
  /** Resolves the signed-in user, or null when there is no valid session. */
  async me(): Promise<AuthenticatedUser | null> {
    try {
      const { user } = await request<UserResponse>('/api/auth/me');
      return user;
    } catch (error) {
      // A 401 here is the normal "not signed in" answer, not a failure.
      if (error instanceof ApiError && error.status === 401) return null;
      throw error;
    }
  },

  async register(input: {
    email: string;
    password: string;
    displayName: string;
  }): Promise<AuthenticatedUser> {
    const { user } = await request<UserResponse>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return user;
  },

  async login(input: { email: string; password: string }): Promise<AuthenticatedUser> {
    const { user } = await request<UserResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return user;
  },

  async logout(): Promise<void> {
    await request<void>('/api/auth/logout', { method: 'POST' });
  },

  /**
   * Succeeds whether or not the address has an account — the server answers
   * identically either way, on purpose. Do not add handling that infers one
   * from the other.
   */
  async requestReset(email: string): Promise<void> {
    await request<void>('/api/auth/request-reset', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  async resetPassword(input: { token: string; password: string }): Promise<void> {
    await request<void>('/api/auth/reset', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
};
